import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/store';
import { useBrowserConnectionStore } from '@/store/browserConnection';
import { useTheme } from '@/context/ThemeContext';
import useDesktopBanners from '@/hooks/useDesktopBanners';
import { EXTENSION_STORE_URL } from '@/constants/browserExtension';
import { extensionBrowserName } from '@/lib/browserExtensionSupport';
import { neutralColors } from '@/constants/neutralColors';
import { isDarkTheme } from '@/constants/Colors';
import '@/app/styles/browser-extension-suggestion.css';

export default function BrowserExtensionSuggestion() {
  const userId = useAuthStore(s => s.token && !s.user?.flags?.isGhost ? s.user?.id : null);
  const installed = useBrowserConnectionStore(s => s.available);
  const desktop = useDesktopBanners();
  const theme = useTheme(), colors = neutralColors(theme.colors, isDarkTheme(theme.theme));
  const [ready, setReady] = useState(false), [dismissed, setDismissed] = useState(true), [browser, setBrowser] = useState(null);
  const storageKey = userId ? `gab:browser-extension-suggestion:${userId}` : null;
  useEffect(() => {
    setReady(false); setBrowser(extensionBrowserName(window.navigator));
    let active = true;
    try { setDismissed(!storageKey || localStorage.getItem(storageKey) === 'dismissed'); }
    catch { setDismissed(!storageKey); }
    // Let the extension's actual handshake arrive before offering installation.
    const timer = setTimeout(() => { if (active) setReady(true); }, 1500);
    if (extensionBrowserName(window.navigator)) {
      Promise.resolve().then(() => window.navigator.brave?.isBrave()).then(brave => {
        if (active && brave) setBrowser('Brave');
      }).catch(() => {});
    }
    return () => { active = false; clearTimeout(timer); };
  }, [storageKey]);
  if (!desktop || !userId || installed || !ready || dismissed || !browser || window.parent !== window) return null;
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(storageKey, 'dismissed'); } catch {}
  };
  return <aside className="be-suggestion" aria-label="Browser extension" style={{
    '--be-ink': colors.text_color_primary, '--be-muted': colors.text_color_secondary,
    '--be-surface': colors.solid_color_primary, '--be-soft': colors.solid_color_secondary,
    '--be-border': colors.border_color_secondary,
  }}>
    <span className="be-icon" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="16" rx="3"/><path d="M3 9h18m-9 3v5m-2.5-2.5h5"/></svg></span>
    <div className="be-copy"><strong>Let Work get things done in your browser</strong><p>Install the extension so Gab AI can use your tabs, fill forms, and more.</p></div>
    <a className="be-install" href={EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">Add to {browser}<span aria-hidden="true">↗</span></a>
    <button className="be-dismiss" type="button" aria-label="Dismiss extension suggestion" title="Hide this suggestion" onClick={dismiss}>×</button>
  </aside>;
}
